import { createApp, reactive } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import ElementPlus from "element-plus";
import zhCn from "element-plus/dist/locale/zh-cn.mjs";
import "element-plus/dist/index.css";
import "./components/common.css";
import "./components/dialogLengthValidation.css";
import "animate.css"
import "flag-icons/css/flag-icons.min.css"
import "@/svgIcon/index";
import svgIcon from "@/svgIcon/index.vue";
import FormUtils from "@/components/view/FormUtils.vue";
import { hasPermission } from "@/components/js/permission";
import Request from '@/api/httpRequest'
import { getDbFieldLengthError, isDialogSubmitDisabled } from '@/components/js/dbFieldLengthUtils'
import { sanitizeHtml } from '@/utils/sanitizeHtml'
import { recoverFromChunkLoadError } from '@/utils/chunkLoadRecovery'
import { startMailAlertSocket } from '@/components/js/mailAlertSocket'

import * as ElementPlusIconsVue from '@element-plus/icons-vue';
/* import 'ant-design-vue/dist/antd.css'; // 引入样式文件 */
const app = createApp(App)
const setSafeHtml = (el, value) => {
    el.innerHTML = sanitizeHtml(value)
}

app.directive('safe-html', {
    mounted(el, binding) {
        setSafeHtml(el, binding.value)
    },
    updated(el, binding) {
        setSafeHtml(el, binding.value)
    }
})
const productCategoryState = reactive({
    map: {},
    loaded: false,
    loading: false
})

const hasAuthToken = () => !!store.state.token

const resetProductCategoryState = () => {
    productCategoryState.map = {}
    productCategoryState.loaded = false
    productCategoryState.loading = false
}

const loadProductCategoryMap = async () => {
    if (!hasAuthToken()) return
    if (productCategoryState.loading || productCategoryState.loaded) return
    productCategoryState.loading = true
    try {
        const res = await Request.getRequest('/api/basic/product/category/list', {
            pageNum: 1,
            pageSize: 200,
            hideLoading: true
        }, { suppressErrorMessage: true })
        const list = Array.isArray(res?.list)
            ? res.list
            : (Array.isArray(res?.data) ? res.data : [])
        const map = {}
        list.forEach(item => {
            const id = item?.id
            const name = item?.name || item?.categoryName || ''
            if (id || id === 0) {
                map[String(id)] = name || String(id)
            }
        })
        productCategoryState.map = map
        productCategoryState.loaded = true
    } catch (error) {
        productCategoryState.map = {}
    } finally {
        productCategoryState.loading = false
    }
}

app.mixin({
    methods: {
        ensureProductCategoryMapLoaded() {
            if (!hasAuthToken()) return Promise.resolve()
            return loadProductCategoryMap()
        },
        getProductCategoryName(categoryValue) {
            if (categoryValue === null || categoryValue === undefined || categoryValue === '') return ''
            const name = productCategoryState.map[String(categoryValue)]
            return name || String(categoryValue)
        },
        formatProductCategory(row, column, cellValue) {
            return this.getProductCategoryName(cellValue)
        },
        getDbFieldLengthError(tableName, fieldName, value) {
            return getDbFieldLengthError(tableName, fieldName, value)
        },
        isDbDialogSubmitDisabled(config) {
            return isDialogSubmitDisabled(config)
        }
    }
})

store.watch(
    state => state.token,
    (newToken, oldToken) => {
        if (!newToken) {
            resetProductCategoryState()
            return
        }
        if (!oldToken || newToken !== oldToken) {
            resetProductCategoryState()
        }
        loadProductCategoryMap()
    },
    { immediate: true }
)

window.addEventListener('error', (event) => {
    const payload = {
        name: event?.error?.name,
        message: event?.message || event?.error?.message,
        stack: event?.error?.stack,
        filename: event?.filename,
        target: event?.target
    }
    recoverFromChunkLoadError(payload)
}, true)

//测试环境拖动控制台报错
if (process.env.NODE_ENV === 'development') {
    const resizeObserverErrorHandler = (event) => {
        const message = event && event.message ? event.message : '';
        const isResizeObserverWarning =
            message.includes('ResizeObserver loop') ||
            message.includes('ResizeObserver loop completed with undelivered notifications');

        if (isResizeObserverWarning) {
            event.stopImmediatePropagation();
        }
    };
    window.addEventListener('error', resizeObserverErrorHandler);
}

//全局引入icon图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component);
}
//自定义指令（节流-防止重复点击）
app.directive('preventReClick', {
    mounted: function (el, binding, vNode, oldVnode) {
        el.addEventListener('click', () => {
            if (!el.disabled) {
                el.disabled = true
                /* el.style.cursor='not-allowed' */
                el.style.cursor='wait'
                setTimeout(() => {
                    el.disabled = false
                    el.style.cursor='pointer'
                }, 2000)
            }
        },
        )
    }
})

const permissionValueKey = Symbol('permissionValue')
const permissionUnwatchKey = Symbol('permissionUnwatch')

function updatePermissionVisibility(el) {
    const allowed = hasPermission(store.state.menuList, el[permissionValueKey])
    el.style.display = allowed ? '' : 'none'
}

// Permission directive: v-permission="'test/add'" or v-permission="['test/add','test/edit']"
app.directive('permission', {
    mounted(el, binding) {
        el[permissionValueKey] = binding.value
        updatePermissionVisibility(el)
        el[permissionUnwatchKey] = store.watch(
            (state) => state.menuList,
            () => updatePermissionVisibility(el)
        )
    },
    updated(el, binding) {
        el[permissionValueKey] = binding.value
        updatePermissionVisibility(el)
    },
    beforeUnmount(el) {
        if (typeof el[permissionUnwatchKey] === 'function') {
            el[permissionUnwatchKey]()
        }
        delete el[permissionUnwatchKey]
        delete el[permissionValueKey]
    }
})


app.use(store).use(router).use(ElementPlus, { locale: zhCn })
    .component('svg-icon', svgIcon)
    .component('form-utils', FormUtils)
    .mount('#app')

startMailAlertSocket(store, router)
